import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { ArrowRight } from "@phosphor-icons/react";

export default function Login() {
  const { user, ready, login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (ready && user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(username.trim().toLowerCase(), password);
      toast.success("Signed in");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#FDFDFD] noise" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 border-r border-neutral-200 p-12 grid-bg">
        <div className="flex items-center gap-3">
          <img
            src="/brand/logo.png"
            alt="FlowFuel"
            className="h-12 w-auto"
            data-testid="login-logo"
          />
          <span className="font-display text-xl tracking-tight border-l border-neutral-300 pl-3">STOCK</span>
        </div>

        <div className="space-y-6">
          <div className="label-eyebrow">V1.3 — FIELD OPS</div>
          <h1 className="font-display text-6xl xl:text-7xl leading-none tracking-tight">
            FlowFuel parts,<br/>tracked with<br/><span className="italic font-light">precision.</span>
          </h1>
          <p className="text-neutral-600 max-w-md leading-relaxed">
            A hard-edged stock ledger for parts issued to field engineers.
            Regions, timestamps, returns — used or unused. Nothing missed.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6 pt-8 border-t border-neutral-200">
          <div>
            <div className="label-eyebrow mb-2">Login roles</div>
            <div className="font-display text-2xl">03</div>
          </div>
          <div>
            <div className="label-eyebrow mb-2">Regions</div>
            <div className="font-display text-2xl">05+</div>
          </div>
          <div>
            <div className="label-eyebrow mb-2">Trace</div>
            <div className="font-display text-2xl">100%</div>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-8" data-testid="login-form">
          <div className="space-y-2">
            <div className="label-eyebrow">Sign in</div>
            <h2 className="font-display text-4xl leading-none tracking-tight">Enter the stock ledger.</h2>
            <p className="text-sm text-neutral-500 pt-2">Use your issued credentials.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="username" className="label-eyebrow">Username or Email</Label>
              <Input
                id="username"
                type="text"
                required
                autoComplete="username"
                data-testid="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="h-11 border-neutral-900 rounded-none focus-visible:ring-0 focus-visible:border-neutral-900 font-mono"
                placeholder="supervisor or tech@company.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="label-eyebrow">Password</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 border-neutral-900 rounded-none focus-visible:ring-0 focus-visible:border-neutral-900"
                placeholder="••••••••••"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full h-11 rounded-none bg-neutral-900 text-neutral-50 hover:bg-neutral-800 font-medium tracking-wide"
          >
            {busy ? "Signing in…" : (<span className="inline-flex items-center gap-2">Sign In <ArrowRight weight="bold" size={16} /></span>)}
          </Button>

          <div className="pt-6 border-t border-neutral-200 text-xs text-neutral-500">
            Access is invite-only. Contact your supervisor for credentials.
          </div>

          <div className="pt-4 space-y-2" data-testid="desktop-shortcuts">
            <div className="label-eyebrow">One-click desktop shortcut</div>
            <div className="grid grid-cols-3 gap-2">
              <a
                href="/downloads/flowfuel-stock-windows.zip"
                download
                data-testid="dl-windows"
                className="border border-neutral-900 px-3 py-2 text-center text-xs font-medium hover:bg-neutral-900 hover:text-white transition-colors"
              >
                Windows
              </a>
              <a
                href="/downloads/flowfuel-stock.webloc"
                download="FlowFuel Stock.webloc"
                data-testid="dl-mac"
                className="border border-neutral-900 px-3 py-2 text-center text-xs font-medium hover:bg-neutral-900 hover:text-white transition-colors"
              >
                macOS
              </a>
              <a
                href="/downloads/flowfuel-stock-linux.zip"
                download
                data-testid="dl-linux"
                className="border border-neutral-900 px-3 py-2 text-center text-xs font-medium hover:bg-neutral-900 hover:text-white transition-colors"
              >
                Linux
              </a>
            </div>
            <p className="text-[10px] text-neutral-500 leading-relaxed">
              Windows / Linux: extract the ZIP to your Desktop.
              macOS: double-click the downloaded <span className="font-mono">.webloc</span>, then drag it to your Desktop.
              Or use your browser&apos;s &quot;Install app&quot; option for a native window.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
